import React, { useState } from 'react'
import { Typography, makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useQuote } from '../../../context'
import type { PresentationOptionV2 } from 'paintscout'
import Scrollspy from 'react-scrollspy'
import { getPresentationNavItems, getQuoteNextPresentation } from '@paintscout/util/builder'
import { usePresentationNav } from '../PresentationNavContext'
import classnames from 'classnames'
import { useClientOptions } from '@paintscout/ui-business'

const useStyles = makeStyles<Theme, PresentationNavTextProps>(
  (theme: Theme) => {
    return {
      textRoot: {
        '&::-webkit-scrollbar-track': {
          boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
          webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)'
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,.1)',
          outline: 'none'
        },
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        overflowX: 'hidden'
      },
      buttonWrapper: {
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.875, 2.5),
        borderLeft: '3px solid transparent',
        background: theme.palette.common.white,
        textAlign: 'left'
      },
      currentItem: {
        borderLeftColor: theme.palette.primary.main,
        background: theme.palette.grey[100]
      },
      button: {
        color: theme.palette.grey[700],
        whiteSpace: 'normal'
      },
      buttonSpan: {
        whiteSpace: 'normal',
        textAlign: 'left',
        hyphens: 'auto'
      },
      currentPage: {
        color: theme.palette.grey[900],
        fontWeight: 600
      },
      currentSection: {
        color: theme.palette.grey[900],
        fontWeight: 600
      },
      highlight: {},
      textDiv: {
        overflow: 'hidden',
        flex: '1 1 auto'
      }
    }
  },
  { name: 'PresentationNavText' }
)

export interface PresentationNavTextProps {
  presentation?: PresentationOptionV2
}

const PresentationNavText = ({ ...props }: PresentationNavTextProps) => {
  const classes = useStyles(props)
  const { options } = useClientOptions()
  const { quote } = useQuote()
  const { currentPage, isQuoteEmpty, onPageClick, view } = usePresentationNav()
  const presentation =
    props?.presentation ??
    getQuoteNextPresentation({
      quote,
      options,
      isWorkOrder: view === 'work-order',
      isProductOrderForm: view === 'product-order-form'
    })
  const [currentScrollPage, setCurrentScrollPage] = useState<string>()

  // page tabs when multi-page, section titles when single-page — see getPresentationNavItems
  const filteredItems = getPresentationNavItems({ presentation, isQuoteEmpty })

  // The item holding the estimate gets highlighted. It deliberately does NOT open the cart — nav
  // items only navigate. Opening the estimate drawer belongs to the explicit affordances: the
  // "View Estimate" buttons in PresentationQuoteDrawer and the cartLink ButtonBlock in QuoteBlock.
  const quoteItemIndex = filteredItems.findIndex((item) => containsQuote(presentation, item.key))
  const highlight = quoteItemIndex === -1 ? null : filteredItems[quoteItemIndex].key

  return (
    <div className={classes.textRoot}>
      <Scrollspy
        componentTag={'div'}
        items={filteredItems.map((item) => item.key)}
        offset={-100}
        onUpdate={(element) => {
          setCurrentScrollPage(element?.id)
        }}
      >
        {filteredItems.map((item, index) => {
          const isActive = isCurrentItem(item, index, currentPage)

          return (
            <div
              className={classnames(classes.buttonWrapper, { [classes.currentItem]: isActive })}
              key={item.key}
              onClick={(ev) => {
                if (onPageClick) {
                  onPageClick(ev, item.key)
                  setCurrentScrollPage(item.key)
                }
              }}
            >
              <div className={classes.textDiv}>
                <Typography
                  classes={{
                    root: classnames({
                      [classes.button]: true,
                      [classes.currentPage]: isActive,
                      [classes.highlight]: highlight === item.key,
                      [classes.buttonSpan]: true
                    })
                  }}
                  variant="overline"
                  noWrap
                >
                  {item.label}
                </Typography>
              </div>
            </div>
          )
        })}
      </Scrollspy>
    </div>
  )

  function isCurrentItem(item, index, currentKey) {
    if (currentScrollPage) {
      return item.key === currentScrollPage
    }
    return (currentKey === null && index === 0) || item.key === currentKey
  }
}

/**
 * Whether the page or section with this key contains the estimate block. The key can be either
 * level — getPresentationNavItems lists pages when multi-page and sections when single-page — so
 * both are checked rather than assuming which one the nav is showing.
 */
function containsQuote(presentation: PresentationOptionV2 | undefined, key: string): boolean {
  for (const page of presentation?.pages ?? []) {
    const sections = page.sections ?? []
    const hasQuote = (candidates: typeof sections) =>
      candidates.some((section) => (section.blocks ?? []).some((block) => block.type === 'quote'))

    if (page.key === key) {
      return hasQuote(sections)
    }

    const section = sections.find((candidate) => candidate.key === key)
    if (section) {
      return hasQuote([section])
    }
  }

  return false
}

export default PresentationNavText
